import { inject, Injectable } from '@angular/core';
import { ApiService } from '@services/api.service';
import { Observable } from 'rxjs';
import {
  ClientWithPermissionsApiResponse,
  SaveClientType,
  ClientConfigOptionsType,
  TenantConfiguration,
  UserTenantsApiResponse,
} from '@shared/types/clients.types';
import { HttpParams } from '@angular/common/http';
import { PaginationParams } from '@shared/types/data-table.types';

@Injectable({
  providedIn: 'root',
})
export class ClientsApiService {
  private apiService = inject(ApiService);

  getClientConfig({
    organizationId,
  }: {
    organizationId: string;
  }): Observable<ClientConfigOptionsType> {
    let params = new HttpParams();
    return this.apiService.get(
      `organizations/${organizationId}/all-configurations`,
      params,
    );
  }

  getUserClientsList(options: {
    organizationId?: string | null;
  }): Observable<UserTenantsApiResponse> {
    let params = new HttpParams();
    return this.apiService.get(
      `user/${options.organizationId}/tenants`,
      params,
    );
  }

  getClientsList(options: {
    organizationId?: string | null;
    paginationParams: PaginationParams;
  }): Observable<ClientWithPermissionsApiResponse> {
    let params = new HttpParams()
      .set('page', options.paginationParams.page.toString())
      .set('pageSize', options.paginationParams.pageSize.toString())
      .set('sortOrder', options.paginationParams.sortOrder);
    if (options.paginationParams.sortBy) {
      params = params.set('sortBy', options.paginationParams.sortBy);
    }

    return this.apiService.get(
      `organizations/${options.organizationId}/tenants`,
      params,
    );
  }

  createNewClient(options: {
    organizationId?: string | null;
    client: SaveClientType;
  }): Observable<any> {
    let params = new HttpParams();

    return this.apiService.post(
      `organizations/${options.organizationId}/tenants`,
      { ...options.client },
      params,
    );
  }

  updateClient(client: SaveClientType): Observable<any> {
    let params = new HttpParams();
    const { tenantID, ...newClient } = client;
    return this.apiService.put(`tenants/${tenantID}`, newClient, params);
  }

  deleteClient(id: string): Observable<any> {
    let params = new HttpParams();
    return this.apiService.delete(`tenants/${id}`, params);
  }

  /**
   * Get tenant configuration including organization info and permissions
   * @param tenantId - The ID of the tenant to get configuration for
   * @returns Observable with tenant configuration containing permissions array
   */
  getTenantConfiguration(tenantId: string): Observable<TenantConfiguration> {
    let params = new HttpParams();
    return this.apiService.get<TenantConfiguration>(
      `tenants/${tenantId}/configuration`,
      params,
    );
  }
}
